import { CameraAlt, Diversity3, People, Sms } from '@mui/icons-material';
import { Button, Grid, Typography, alpha } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { Link } from 'react-router-dom';
import React from 'react';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';

interface Report {
  label: string;
  description: string;
  route: RoutesEnum;
  icon: React.ElementType;
  category: 'members' | 'other';
  permission?: PermissionEnum;
}

type ReportPickerProps = {
  category: 'members' | 'other';
};

export const ReportPicker = ({ category }: ReportPickerProps) => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();

  const allReports: Report[] = [
    {
      label: t('statistics:report.snapshot'),
      description: t('statistics:report.snapshotDescription'),
      route: RoutesEnum.StatisticsReportsSnapshot,
      icon: CameraAlt,
      category: 'members',
    },
    {
      label: t('statistics:report.membersCount'),
      description: t('statistics:report.membersCountDescription'),
      route: RoutesEnum.StatisticsReportsMembers,
      icon: People,
      category: 'members',
    },
    {
      label: t('statistics:report.ageAndGenderDistribution'),
      description: t('statistics:report.ageAndGenderDistributionDescription'),
      route: RoutesEnum.StatisticsAgeAndGenderDistribution,
      icon: Diversity3,
      category: 'members',
    },
    {
      label: t('statistics:report.smsService'),
      description: t('statistics:report.smsServiceDescription'),
      route: RoutesEnum.StatisticsReportsSmsService,
      icon: Sms,
      category: 'other',
      permission: PermissionEnum.InvoiceCreate,
    },
  ];

  const reports = allReports.filter((r) => r.category === category && getPermission(currentUser, r.permission ?? ''));

  return (
    <>
      <GridWrapper>
        {reports.map((rep) => (
          <Grid item xs={12} md={6} lg={4} key={rep.route}>
            <Button
              component={Link}
              to={rep.route}
              color='primary'
              variant='outlined'
              fullWidth
              sx={{
                py: 2,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                flexDirection: 'row',
                height: '100%',
                backgroundColor: '#fff',
                position: 'relative',
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.success.light, 0.2),
                },
                '&.selected': {
                  borderWidth: 3,
                  backgroundColor: (theme) => alpha(theme.palette.success.light, 0.2),
                },
              }}
            >
              <Box mr={1}>
                <rep.icon sx={{ width: '2rem', opacity: 0.8 }} />
              </Box>

              <Box>
                <Typography
                  variant='body1'
                  component='div'
                  sx={{
                    fontWeight: 550,
                    textTransform: 'none',
                    textAlign: 'left',
                  }}
                >
                  {t(rep.label)}
                </Typography>
                <Typography
                  variant='body2'
                  component='div'
                  sx={{
                    fontWeight: 100,
                    textTransform: 'none',
                    textAlign: 'left',
                    mt: 1,
                  }}
                >
                  {t(rep.description)}
                </Typography>
              </Box>
            </Button>
          </Grid>
        ))}
      </GridWrapper>
    </>
  );
};
